import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { getServices, getWallets } from "@/data/site";
import { Github, Linkedin, Twitter, MapPin, Mail, Building2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

const Col = ({ title, links }) => (
  <div>
    <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-4">{title}</h4>
    <ul className="space-y-2.5">
      {links.map((l) => (
        <li key={l.to}>
          <Link to={l.to} className="text-sm text-slate-400 hover:text-white transition-colors" data-testid={`footer-link-${l.to}`}>{l.label}</Link>
        </li>
      ))}
    </ul>
  </div>
);

function CompanySection({ t }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/public/company").then(({ data }) => setC(data)).catch(() => setC(null)); }, []);
  if (!c || !c.legal_company_name || !c.registered_address || !c.kvk_number) return null;
  return (
    <section className="border-t border-white/10 bg-navy" data-testid="company-info">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <h3 className="font-heading text-xl text-white flex items-center gap-2"><Building2 className="h-5 w-5 text-gold" />{t("company.title")}</h3>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-1.5">
            <p className="text-white font-medium">{c.legal_company_name}{c.legal_form ? ` · ${c.legal_form}` : ""}</p>
            <p className="text-slate-400 flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-cyanx" />{c.registered_address}</p>
            <p className="text-slate-400">{t("company.reg")}: <span className="text-slate-200">{c.kvk_number}</span></p>
            {c.vat_number && <p className="text-slate-400">{t("company.vat")}: <span className="text-slate-200">{c.vat_number}</span></p>}
            {c.legal_email && <p className="text-slate-400 flex items-center gap-2"><Mail className="h-4 w-4 text-cyanx" /><a href={`mailto:${c.legal_email}`} className="text-slate-200 hover:text-white">{c.legal_email}</a></p>}
          </div>
          {c.maps_url && (
            <div className="flex items-start">
              <a href={c.maps_url} target="_blank" rel="noopener noreferrer" data-testid="company-directions" className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-5 h-11 text-sm text-white hover:bg-white/5">
                <MapPin className="h-4 w-4 text-gold" />{t("company.directions")}
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function PublicFooter() {
  const { t, lang } = useI18n();
  return (
    <>
      <CompanySection t={t} />
      <footer className="relative border-t border-white/10 bg-navy-paper" data-testid="public-footer">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
            <div className="col-span-2">
              <Logo />
              <p className="mt-4 text-sm text-slate-400 max-w-xs leading-relaxed">{t("footer.tagline")}</p>
              <div className="mt-5 flex gap-3">
                {[Twitter, Linkedin, Github].map((Icon, i) => (
                  <span key={i} className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-500" title="Social placeholder"><Icon className="h-4 w-4" /></span>
                ))}
              </div>
            </div>
            <Col title={t("footer.services")} links={getServices(lang).map((s) => ({ to: `/servizi/${s.slug}`, label: s.title }))} />
            <Col title={t("footer.wallets")} links={getWallets(lang).slice(0, 6).map((w) => ({ to: `/wallet/${w.slug}`, label: w.name }))} />
            <Col title={t("footer.resources")} links={[
              { to: "/come-funziona", label: t("footer.how") },
              { to: "/prezzi", label: t("footer.pricing") },
              { to: "/blog", label: t("footer.blog") },
              { to: "/faq", label: t("nav.faq") },
              { to: "/recensioni", label: t("footer.reviews") },
            ]} />
            <Col title={t("footer.legal")} links={[
              { to: "/termini", label: t("footer.terms") },
              { to: "/privacy", label: t("footer.privacy") },
              { to: "/cookie-policy", label: t("footer.cookie") },
              { to: "/disclaimer", label: t("footer.disclaimer_link") },
              { to: "/reclami", label: t("footer.complaints") },
              { to: "/contatti", label: t("footer.contact") },
            ]} />
          </div>
          <div className="mt-14 pt-6 border-t border-white/10 flex flex-col gap-4">
            <a href="https://eurla.eu/" target="_blank" rel="noopener noreferrer" data-testid="eurla-link-footer" className="text-sm text-gold hover:text-gold-hover w-fit">{t("footer.eurla")}</a>
            <p className="text-xs text-slate-500 leading-relaxed max-w-4xl" data-testid="footer-disclaimer">{t("footer.disclaimer")}</p>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-slate-500">© {new Date().getFullYear()} Recovers Crypto. {t("footer.rights")}</p>
              <a href="mailto:support@recoverscrypto.com" data-testid="footer-contact-email" className="text-xs text-slate-500 hover:text-cyanx transition-colors">support@recoverscrypto.com</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
